import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit, inject } from '@angular/core';
import { BehaviorSubject, Subject } from 'rxjs';
import { map, takeUntil } from 'rxjs/operators';
import { wsListenerACTIVE_PROJECT_USERS, MtmStompService } from '../../../services/mtm-stomp.service';
import { HelperService } from '../../../services/helper.service';
import { StringUtilsService } from '../../../services/string-utils.service';
import { onLoadProject } from '../../../services/project.service';

interface Project {
  id: string;
  participants: ProjectParticipant[];
}

interface ProjectParticipant {
  id?: string;
  username: string;
  fullName: string;
  avatar?: string;
  online?: boolean;
  rsvp?: string;
}

@Component({
  selector: 'mtm-user-list-dropdown',
  templateUrl: './mtm-user-list-dropdown.component.html',
  styleUrl: './mtm-user-list-dropdown.component.scss',
  changeDetection: ChangeDetectionStrategy.OnPush,
  standalone: false,
})
export class MtmUserListDropdownComponent implements OnInit, OnDestroy {
  private readonly stompService = inject(MtmStompService);
  private readonly cdr = inject(ChangeDetectorRef);
  @Input() project!: Project;

  private users: ProjectParticipant[] = [];
  private readonly usersSubject = new BehaviorSubject<ProjectParticipant[]>([]);
  private readonly stringUtilsService = inject(StringUtilsService);
  searchQuery = '';
  filteredUsers: ProjectParticipant[] = [];

  private readonly wsId: string;
  private readonly destroy$ = new Subject<void>();

  constructor() {
    this.wsId = this.stompService.subscribeToService();

    this.usersSubject
      .asObservable()
      .pipe(
        takeUntil(this.destroy$),
        map((users) => this.filterAndSortUsers(users, this.searchQuery))
      )
      .subscribe((users) => {
        this.filteredUsers = users;
        this.cdr.markForCheck();
      });
  }

  ngOnInit(): void {
    this.processParticipants();
    this.subscribeToOnlineUsers();
    // project.participants is client-hydrated asynchronously during project load; on a
    // subsection deep-link this component can mount before it lands. Re-process once the
    // load pipeline signals completion (participants are guaranteed set by then).
    onLoadProject.pipe(takeUntil(this.destroy$)).subscribe(() => this.processParticipants());
  }

  ngOnDestroy(): void {
    this.destroy$.next();
    this.destroy$.complete();
    this.stompService.unsubscribeToService(this.wsId);
  }

  onSearchChange(query: string): void {
    this.searchQuery = query;
    this.usersSubject.next(this.users);
  }

  private processParticipants(): void {
    if (!this.project?.participants?.length) {
      this.users = [];
      this.usersSubject.next([]);
      return;
    }
    this.users = this.project.participants.filter(this.filterParticipant);
    this.usersSubject.next(this.users);
  }

  private filterParticipant(user: ProjectParticipant): boolean {
    return user.rsvp?.toLowerCase() === 'yes';
  }

  private readonly setUserStatus = (onlineUsers: string[]): void => {
    try {
      const updatedUsers = this.users.map((user) => ({
        ...user,
        online: onlineUsers.includes(user.username),
      }));
      this.users = updatedUsers;
      this.usersSubject.next(updatedUsers);
    } catch (err) {
      //console.error('Error updating user status:', err);
    }
  };

  private subscribeToOnlineUsers(): void {
    if (!this.project?.id) return;
    this.stompService.subscribeToListener(
      'ACTIVE_PROJECT_USERS',
      this.wsId,
      [this.project.id],
      this.setUserStatus,
      wsListenerACTIVE_PROJECT_USERS
    );
  }

  private filterAndSortUsers(users: ProjectParticipant[], query: string): ProjectParticipant[] {
    let filtered = users;
    if (query) {
      const normalizedQuery = this.stringUtilsService.getNormalizedName(query);
      filtered = users.filter(
        (user) =>
          this.stringUtilsService.getNormalizedName(user.fullName).includes(normalizedQuery) ||
          this.stringUtilsService.getNormalizedName(user.username).includes(normalizedQuery)
      );
    }
    return filtered.sort((a, b) => {
      if (a.online && !b.online) return -1;
      if (!a.online && b.online) return 1;
      return a.fullName.localeCompare(b.fullName);
    });
  }
}
